import { zodResolver } from "@hookform/resolvers/zod";
import { useMemo } from "react";
import {
  useForm,
  Control,
  UseFormHandleSubmit,
  UseFormReturn,
  UseFormGetValues,
} from "react-hook-form";
import { object, string, z } from "zod";

import { useT } from "src/internationalization";
import { AlertValidationDecision } from "src/types/alert";

export type FraudDetailsFormControl = {
  control: Control<FraudDetailsFormData>;
  submit: UseFormHandleSubmit<FraudDetailsFormData>;
  reset: () => void;
  hasError: boolean;
  methods: UseFormReturn<FraudDetailsFormData>;
  getValues: UseFormGetValues<FraudDetailsFormData>;
};

type FraudDetailsFormData = {
  comment: string;
  fraudType: {
    code: string;
    name: string;
  } | null;
  amount?: number;
};

export type FraudDetailsFormValidatedData = {
  comment: string;
  fraudType: {
    code: string;
    name: string;
  };
  amount?: number;
};

const DEFAULT_VALUES = {
  comment: "",
  fraudType: null,
  amount: 0,
};

export const useFraudDetailsForm = ({
  decision,
}: {
  decision: AlertValidationDecision | null;
}): FraudDetailsFormControl => {
  const t = useT();

  const validationSchema = useMemo(
    () =>
      object({
        comment: string().min(1, t("general.form.comment.error.required")),
        fraudType:
          decision === "compliant"
            ? z.any().optional()
            : object(
                {
                  code: string(),
                  name: string(),
                },
                { message: t("alerts.confirmFraud.details.form.fraudType.error.required") }
              ),
        amount:
          decision === "fraudPrevented" || decision === "provenFraudWithFinancialLoss"
            ? z.preprocess(
                (val) => (val === "" || val === undefined || val === null ? null : Number(val)),
                z
                  .number({
                    message:
                      decision === "fraudPrevented"
                        ? t("alerts.confirmFraud.details.form.amount.prevented.error.required")
                        : t("alerts.confirmFraud.details.form.amount.suffered.error.required"),
                  })
                  .min(0, { message: t("alerts.confirmFraud.details.form.amount.error.min") })
              )
            : z.any().optional(),
      }),
    [t, decision]
  );

  const methods = useForm<FraudDetailsFormData>({
    resolver: zodResolver(validationSchema),
    defaultValues: DEFAULT_VALUES,
  });

  const {
    control,
    handleSubmit,
    formState: { isValid, isSubmitted },
  } = methods;

  const reset = () => {
    methods.reset({
      comment: "",
      fraudType: null,
      amount: 0,
    });
  };

  return {
    control,
    submit: handleSubmit,
    reset,
    hasError: isSubmitted ? !isValid : false,
    methods,
    getValues: methods.getValues,
  };
};
